import PropTypes from 'prop-types';
// @mui
import { styled, alpha } from '@mui/material/styles';
import { Typography, Box, Stack } from '@mui/material';
// components
import Logo from '../components/Logo';
import useAuth from '../hooks/useAuth';
import { APP_NAME } from '../config';

// ----------------------------------------------------------------------

const RootStyle = styled('div')(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
    [theme.breakpoints.up('md')]: {
        flexDirection: 'row',
        height: '100vh',
        overflow: 'hidden',
    },
}));

const SectionStyle = styled('div')(({ theme }) => ({
    display: 'none',
    [theme.breakpoints.up('md')]: {
        width: '50%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 50%, #0c1221 100%)`,
        overflow: 'hidden',
    },
}));

const ContentStyle = styled('div')(({ theme }) => ({
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    backgroundColor: theme.palette.background.default,
    overflowY: 'auto',
    height: '100%',
    padding: theme.spacing(4, 4),
    [theme.breakpoints.up('md')]: {
        padding: theme.spacing(6, 12),
    },
}));

const LogoStyle = styled(Box)(({ theme }) => ({
    top: theme.spacing(4),
    left: theme.spacing(4),
    zIndex: 9,
    position: 'absolute',
}));

const GlassCard = styled(Box)(({ theme }) => ({
    position: 'relative',
    width: '80%',
    maxWidth: 480,
    padding: theme.spacing(5),
    borderRadius: 24,
    backgroundColor: alpha(theme.palette.primary.main, 0.35), // brand blue color for background translucency
    backdropFilter: 'blur(20px) saturate(180%)',
    WebkitBackdropFilter: 'blur(20px) saturate(180%)',
    border: '1px solid rgba(255, 255, 255, 0.12)',
    color: theme.palette.common.white,
    boxShadow: '0 30px 60px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.15)',
    zIndex: 10,
}));

// ----------------------------------------------------------------------

AuthLayout.propTypes = {
    children: PropTypes.node,
    title: PropTypes.string,
};

export default function AuthLayout({ children }) {
    const { setting } = useAuth();
    const appName = setting?.general?.app_name || APP_NAME;

    return (
        <RootStyle>
            <SectionStyle>
                {/* Logo top-left inside left section */}
                <LogoStyle>
                     <Logo sx={{ width: 50, height: 50 }} />
                </LogoStyle>

                {/* Ambient glowing circles */}
                <Box sx={{
                    position: 'absolute',
                    top: '-10%',
                    left: '-10%',
                    width: '60%',
                    height: '60%',
                    borderRadius: '50%',
                    background: (theme) => `radial-gradient(circle, ${alpha(theme.palette.primary.light, 0.25)} 0%, transparent 70%)`,
                    filter: 'blur(80px)',
                    zIndex: 1
                }} />
                <Box sx={{
                    position: 'absolute',
                    bottom: '-15%',
                    right: '-15%',
                    width: '70%',
                    height: '70%',
                    borderRadius: '50%',
                    background: (theme) => `radial-gradient(circle, ${alpha(theme.palette.primary.main, 0.15)} 0%, transparent 70%)`,
                    filter: 'blur(100px)',
                    zIndex: 1
                }} />
                <Box sx={{
                    position: 'absolute',
                    top: '-30%',
                    right: '-30%',
                    width: '120%',
                    height: '120%',
                    opacity: 0.1,
                    background: (theme) => `linear-gradient(45deg, transparent 40%, ${theme.palette.primary.light} 45%, ${theme.palette.primary.main} 55%, transparent 60%)`,
                    transform: 'rotate(-15deg)',
                    zIndex: 1
                }} />

                {/* Centered Glass Card */}
                <GlassCard>
                     <Typography variant="body2" sx={{ fontWeight: 600, letterSpacing: 1.5, textTransform: 'uppercase', fontSize: '0.75rem', opacity: 0.9, mb: 4 }}>
                        + Fast, Secure & Automated VTU
                     </Typography>
                     
                     <Typography variant="h3" sx={{ mb: 3, fontWeight: 800, lineHeight: 1.2 }}>
                        Maximize Your VTU & Telecom Business
                     </Typography>
                     
                     <Typography sx={{ opacity: 0.8, fontSize: '0.975rem', lineHeight: 1.6, mb: 5 }}>
                        Get instant access to cheap data, automated airtime top-ups, swift utility bill payments, and educational pins. Connect to our high-performance API for all your telecom needs.
                     </Typography>

                     <Stack direction="row" justifyContent="space-between" alignItems="flex-end" sx={{ pt: 3.5, borderTop: '1px solid rgba(255,255,255,0.12)' }}>
                        <Box>
                            <Typography variant="caption" sx={{ opacity: 0.7, display: 'block', fontSize: '0.75rem', mb: 0.5 }}>
                                API Uptime
                            </Typography>
                            <Typography variant="h4" sx={{ fontWeight: 800 }}>
                                99.9%
                            </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                            <Typography variant="caption" sx={{ opacity: 0.7, display: 'block', fontSize: '0.75rem', mb: 0.5 }}>
                                Transactions Processed
                            </Typography>
                            <Typography variant="h4" sx={{ fontWeight: 800 }}>
                                10M+
                            </Typography>
                        </Box>
                     </Stack>
                     
                     <Typography variant="h5" sx={{ position: 'absolute', bottom: 15, right: 15, opacity: 0.25, fontWeight: 300, userSelect: 'none' }}>
                        +
                     </Typography>
                </GlassCard>
            </SectionStyle>

            <ContentStyle>
                {/* Logo for Mobile Only */}
                <Box sx={{ display: { xs: 'block', md: 'none' }, mb: 4, textAlign: 'center' }}>
                    <Logo sx={{ mx: 'auto' }} />
                </Box>

                <Box sx={{ width: 1, maxWidth: 420, margin: 'auto', display: 'flex', flexDirection: 'column', height: '100%', justifyContent: 'center' }}>
                    {children}
                    
                    <Box sx={{ mt: 5, textAlign: 'center' }}>
                        <Typography variant="caption" sx={{ color: 'text.secondary', opacity: 0.8 }}>
                            © {appName} {new Date().getFullYear()}. All rights reserved.
                        </Typography>
                    </Box>
                </Box>
            </ContentStyle>
        </RootStyle>
    );
}
